// Admin Analytics — real GA4 detailed report + Gemini "what to do" assistant.
// Data comes from functions/lib/analytics/ga4.js via src/services/analyticsReport.js.

import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  ArcElement,
  BarElement,
  Tooltip,
  Legend,
  Filler,
} from 'chart.js';
import { Line, Doughnut, Bar } from 'react-chartjs-2';
import {
  Users,
  UserPlus,
  MousePointerClick,
  Eye,
  Timer,
  Activity,
  TrendingUp,
  TrendingDown,
  Sparkles,
  RefreshCw,
  AlertCircle,
} from 'lucide-react';
import { fetchGa4Report, fetchGa4Advice } from '@/services/analyticsReport';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  ArcElement,
  BarElement,
  Tooltip,
  Legend,
  Filler
);

// ---------------------------------------------------------------------------
// Formatting helpers
// ---------------------------------------------------------------------------

const fmtInt = (n) => Number(n ?? 0).toLocaleString();
const fmtPct = (n) => `${(Number(n ?? 0) * 100).toFixed(1)}%`;
const fmtDuration = (secs) => {
  const s = Math.round(Number(secs ?? 0));
  return `${Math.floor(s / 60)}m ${s % 60}s`;
};
// GA4 returns dates as YYYYMMDD
const fmtDate = (d) => `${d.slice(4, 6)}/${d.slice(6, 8)}`;

function deltaPct(current, previous) {
  const prev = Number(previous ?? 0);
  if (!prev) return null;
  return ((Number(current ?? 0) - prev) / prev) * 100;
}

// ---------------------------------------------------------------------------
// Small presentational pieces
// ---------------------------------------------------------------------------

function StatCard({ icon: Icon, label, value, delta, invertDelta = false, color }) {
  const good = delta !== null && (invertDelta ? delta < 0 : delta > 0);
  return (
    <div className="bg-white rounded-2xl shadow-sm p-5 flex items-center gap-4">
      <div className={`w-11 h-11 rounded-xl flex items-center justify-center shrink-0 ${color}`}>
        <Icon size={20} className="text-white" />
      </div>
      <div className="min-w-0">
        <p className="text-xs text-gray-500 truncate">{label}</p>
        <p className="text-xl font-bold text-gray-800">{value}</p>
        {delta !== null && (
          <p
            className={`text-xs font-medium flex items-center gap-1 ${
              good ? 'text-emerald-600' : 'text-red-500'
            }`}
          >
            {delta >= 0 ? <TrendingUp size={12} /> : <TrendingDown size={12} />}
            {Math.abs(delta).toFixed(1)}% vs prev. period
          </p>
        )}
      </div>
    </div>
  );
}

function Panel({ title, children, className = '' }) {
  return (
    <div className={`bg-white rounded-2xl shadow-sm p-6 ${className}`}>
      <h2 className="text-sm font-semibold text-gray-700 mb-4">{title}</h2>
      {children}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------

const RANGES = [
  { days: 7, label: '7 days' },
  { days: 28, label: '28 days' },
  { days: 90, label: '90 days' },
];

export default function AdminAnalytics() {
  const [days, setDays] = useState(28);
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [advice, setAdvice] = useState(null);
  const [adviceLoading, setAdviceLoading] = useState(false);
  const [adviceError, setAdviceError] = useState(null);

  const loadReport = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setReport(await fetchGa4Report(days));
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [days]);

  useEffect(() => {
    loadReport();
    setAdvice(null);
    setAdviceError(null);
  }, [loadReport]);

  const loadAdvice = async () => {
    setAdviceLoading(true);
    setAdviceError(null);
    try {
      setAdvice(await fetchGa4Advice(days));
    } catch (err) {
      setAdviceError(err.message);
    } finally {
      setAdviceLoading(false);
    }
  };

  // ----- chart data ---------------------------------------------------------

  const trendData = useMemo(() => {
    if (!report) return null;
    return {
      labels: report.daily.map((r) => fmtDate(r.date)),
      datasets: [
        {
          label: 'Active users',
          data: report.daily.map((r) => r.activeUsers),
          borderColor: '#1E3A8A',
          backgroundColor: 'rgba(30, 58, 138, 0.08)',
          fill: true,
          tension: 0.35,
          pointRadius: 2,
        },
        {
          label: 'Page views',
          data: report.daily.map((r) => r.screenPageViews),
          borderColor: '#14B8A6',
          backgroundColor: 'transparent',
          tension: 0.35,
          pointRadius: 2,
        },
      ],
    };
  }, [report]);

  const deviceData = useMemo(() => {
    if (!report) return null;
    return {
      labels: report.devices.map((r) => r.deviceCategory),
      datasets: [
        {
          data: report.devices.map((r) => r.activeUsers),
          backgroundColor: ['#1E3A8A', '#14B8A6', '#F59E0B', '#7C3AED'],
          borderWidth: 0,
        },
      ],
    };
  }, [report]);

  const sourceData = useMemo(() => {
    if (!report) return null;
    return {
      labels: report.sources.map((r) => r.sessionDefaultChannelGroup),
      datasets: [
        {
          label: 'Sessions',
          data: report.sources.map((r) => r.sessions),
          backgroundColor: '#1E3A8A',
          borderRadius: 6,
        },
      ],
    };
  }, [report]);

  // ----- render -------------------------------------------------------------

  const t = report?.totals;

  return (
    <div className="p-8">
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-4 mb-8">
        <div>
          <h1 className="text-2xl font-bold text-gray-800 mb-1">Analytics</h1>
          <p className="text-gray-500 text-sm">
            Real GA4 data{report ? ` · property ${report.propertyId}` : ''}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <div className="bg-white rounded-xl shadow-sm p-1 flex">
            {RANGES.map(({ days: d, label }) => (
              <button
                key={d}
                onClick={() => setDays(d)}
                className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                  days === d ? 'bg-[#1E3A8A] text-white' : 'text-gray-600 hover:bg-gray-100'
                }`}
              >
                {label}
              </button>
            ))}
          </div>
          <button
            onClick={loadReport}
            disabled={loading}
            className="p-2.5 bg-white rounded-xl shadow-sm text-gray-600 hover:bg-gray-100 disabled:opacity-50"
            aria-label="Refresh"
          >
            <RefreshCw size={16} className={loading ? 'animate-spin' : ''} />
          </button>
        </div>
      </div>

      {/* Error state */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-2xl p-5 mb-8 flex items-start gap-3">
          <AlertCircle size={20} className="text-red-500 shrink-0 mt-0.5" />
          <div>
            <p className="font-medium text-red-700">Could not load GA4 data</p>
            <p className="text-sm text-red-600 mt-1">{error}</p>
            <p className="text-xs text-red-500 mt-2">
              Check that GA4_PROPERTY_ID is configured and the functions service account has
              Viewer access on the GA4 property.
            </p>
          </div>
        </div>
      )}

      {/* Loading skeleton */}
      {loading && !report && (
        <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-5">
          {Array.from({ length: 8 }).map((_, i) => (
            <div key={i} className="bg-white rounded-2xl shadow-sm p-5 h-24 animate-pulse" />
          ))}
        </div>
      )}

      {report && t && (
        <>
          {/* Stat cards */}
          <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-5 mb-8">
            <StatCard
              icon={Users}
              label="Active users"
              value={fmtInt(t.current.activeUsers)}
              delta={deltaPct(t.current.activeUsers, t.previous.activeUsers)}
              color="bg-[#1E3A8A]"
            />
            <StatCard
              icon={UserPlus}
              label="New users"
              value={fmtInt(t.current.newUsers)}
              delta={deltaPct(t.current.newUsers, t.previous.newUsers)}
              color="bg-[#14B8A6]"
            />
            <StatCard
              icon={MousePointerClick}
              label="Sessions"
              value={fmtInt(t.current.sessions)}
              delta={deltaPct(t.current.sessions, t.previous.sessions)}
              color="bg-[#7C3AED]"
            />
            <StatCard
              icon={Eye}
              label="Page views"
              value={fmtInt(t.current.screenPageViews)}
              delta={deltaPct(t.current.screenPageViews, t.previous.screenPageViews)}
              color="bg-[#F59E0B]"
            />
            <StatCard
              icon={Timer}
              label="Avg. session duration"
              value={fmtDuration(t.current.averageSessionDuration)}
              delta={deltaPct(t.current.averageSessionDuration, t.previous.averageSessionDuration)}
              color="bg-[#0EA5E9]"
            />
            <StatCard
              icon={Activity}
              label="Engagement rate"
              value={fmtPct(t.current.engagementRate)}
              delta={deltaPct(t.current.engagementRate, t.previous.engagementRate)}
              color="bg-[#10B981]"
            />
            <StatCard
              icon={TrendingDown}
              label="Bounce rate"
              value={fmtPct(t.current.bounceRate)}
              delta={deltaPct(t.current.bounceRate, t.previous.bounceRate)}
              invertDelta
              color="bg-[#EF4444]"
            />
            <StatCard
              icon={Eye}
              label="Views / session"
              value={
                t.current.sessions
                  ? (t.current.screenPageViews / t.current.sessions).toFixed(1)
                  : '0'
              }
              delta={null}
              color="bg-[#6366F1]"
            />
          </div>

          {/* AI assistant */}
          <Panel
            title=""
            className="mb-8 border border-[#1E3A8A]/10 bg-gradient-to-br from-white to-indigo-50/50"
          >
            <div className="flex flex-wrap items-center justify-between gap-3 mb-1">
              <div className="flex items-center gap-2">
                <Sparkles size={18} className="text-[#7C3AED]" />
                <h2 className="text-sm font-semibold text-gray-800">
                  AI Assistant — what should I do?
                </h2>
              </div>
              <button
                onClick={loadAdvice}
                disabled={adviceLoading}
                className="px-4 py-2 bg-[#7C3AED] text-white text-sm font-medium rounded-xl hover:bg-[#6D28D9] disabled:opacity-60 flex items-center gap-2"
              >
                {adviceLoading ? (
                  <>
                    <RefreshCw size={14} className="animate-spin" /> Analyzing…
                  </>
                ) : (
                  <>
                    <Sparkles size={14} /> {advice ? 'Re-analyze' : 'Analyze my data'}
                  </>
                )}
              </button>
            </div>

            {!advice && !adviceLoading && !adviceError && (
              <p className="text-sm text-gray-500">
                Gemini reads this exact GA4 report and returns a prioritized action plan —
                what to fix, what to double down on, and why.
              </p>
            )}
            {adviceError && <p className="text-sm text-red-600">{adviceError}</p>}

            {advice && (
              <div className="mt-3">
                <p className="text-sm text-gray-700 bg-white/70 rounded-xl p-4 mb-4">
                  {advice.summary}
                </p>
                <ol className="space-y-3">
                  {(advice.actions || []).map((a) => (
                    <li key={a.priority} className="bg-white rounded-xl p-4 shadow-sm flex gap-4">
                      <span className="w-7 h-7 rounded-full bg-[#1E3A8A] text-white text-sm font-bold flex items-center justify-center shrink-0">
                        {a.priority}
                      </span>
                      <div>
                        <p className="font-semibold text-gray-800 text-sm">{a.title}</p>
                        <p className="text-xs text-gray-500 mt-1">
                          <span className="font-medium text-gray-600">Why:</span> {a.why}
                        </p>
                        <p className="text-xs text-gray-500 mt-1">
                          <span className="font-medium text-gray-600">How:</span> {a.how}
                        </p>
                      </div>
                    </li>
                  ))}
                </ol>
              </div>
            )}
          </Panel>

          {/* Trend + devices */}
          <div className="grid grid-cols-1 xl:grid-cols-3 gap-5 mb-8">
            <Panel title={`Daily trend (last ${days} days)`} className="xl:col-span-2">
              <div className="h-72">
                <Line
                  data={trendData}
                  options={{
                    responsive: true,
                    maintainAspectRatio: false,
                    plugins: { legend: { position: 'bottom', labels: { boxWidth: 12 } } },
                    scales: { y: { beginAtZero: true } },
                  }}
                />
              </div>
            </Panel>
            <Panel title="Devices">
              <div className="h-72 flex items-center justify-center">
                <Doughnut
                  data={deviceData}
                  options={{
                    responsive: true,
                    maintainAspectRatio: false,
                    plugins: { legend: { position: 'bottom', labels: { boxWidth: 12 } } },
                  }}
                />
              </div>
            </Panel>
          </div>

          {/* Sources + countries */}
          <div className="grid grid-cols-1 xl:grid-cols-2 gap-5 mb-8">
            <Panel title="Traffic channels (sessions)">
              <div className="h-64">
                <Bar
                  data={sourceData}
                  options={{
                    indexAxis: 'y',
                    responsive: true,
                    maintainAspectRatio: false,
                    plugins: { legend: { display: false } },
                    scales: { x: { beginAtZero: true } },
                  }}
                />
              </div>
            </Panel>
            <Panel title="Top countries">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-xs text-gray-400 border-b">
                    <th className="pb-2 font-medium">Country</th>
                    <th className="pb-2 font-medium text-right">Users</th>
                    <th className="pb-2 font-medium text-right">Sessions</th>
                  </tr>
                </thead>
                <tbody>
                  {report.countries.map((r) => (
                    <tr key={r.country} className="border-b border-gray-50 last:border-0">
                      <td className="py-2 text-gray-700">{r.country}</td>
                      <td className="py-2 text-right text-gray-700">{fmtInt(r.activeUsers)}</td>
                      <td className="py-2 text-right text-gray-500">{fmtInt(r.sessions)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </Panel>
          </div>

          {/* Top pages */}
          <Panel title="Top pages" className="mb-8">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-xs text-gray-400 border-b">
                    <th className="pb-2 font-medium">Page</th>
                    <th className="pb-2 font-medium text-right">Views</th>
                    <th className="pb-2 font-medium text-right">Users</th>
                    <th className="pb-2 font-medium text-right">Avg. duration</th>
                    <th className="pb-2 font-medium text-right">Engagement</th>
                  </tr>
                </thead>
                <tbody>
                  {report.topPages.map((r) => (
                    <tr key={r.pagePath} className="border-b border-gray-50 last:border-0">
                      <td className="py-2 pr-4 max-w-xs">
                        <p className="text-gray-700 truncate">{r.pageTitle || r.pagePath}</p>
                        <p className="text-xs text-gray-400 truncate">{r.pagePath}</p>
                      </td>
                      <td className="py-2 text-right text-gray-700">{fmtInt(r.screenPageViews)}</td>
                      <td className="py-2 text-right text-gray-500">{fmtInt(r.activeUsers)}</td>
                      <td className="py-2 text-right text-gray-500">
                        {fmtDuration(r.averageSessionDuration)}
                      </td>
                      <td className="py-2 text-right text-gray-500">{fmtPct(r.engagementRate)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Panel>

          {/* Events */}
          <Panel title="Top events">
            <div className="flex flex-wrap gap-2">
              {report.events.map((r) => (
                <span
                  key={r.eventName}
                  className="px-3 py-1.5 bg-gray-50 rounded-lg text-xs text-gray-600"
                >
                  <span className="font-medium text-gray-800">{r.eventName}</span>{' '}
                  {fmtInt(r.eventCount)}
                </span>
              ))}
            </div>
          </Panel>
        </>
      )}
    </div>
  );
}
